'use client';

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { useRouter } from '@/i18n/navigation';
import type { useToast } from '@/contexts/ToastContext';
import type { useTranslations } from 'next-intl';
import type { AuthUser } from '@/contexts/AuthContext';
import { fetchWithAuth } from '@/lib/utils/api-auth';
import type { Project as ProjectSummary } from '@/types/project';
import type { Workspace } from '@/types/shared/org';
import type { ProjectFolder } from '@/components/home/theme-editor';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Pull a human-readable error string out of an API error envelope, falling back
 * to `fallback` when neither `message` nor `error` is present. Exported so the
 * provider's folder actions (and, later, `useDashboardFolderActions`) share the
 * exact same extraction the projects loader uses.
 */
export function extractApiErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const message =
      (typeof record.message === 'string' && record.message.trim()) ||
      (typeof record.error === 'string' && record.error.trim()) ||
      '';
    if (message) return message;
  }
  return fallback;
}

interface UseDashboardProjectsParams {
  /** Resolved auth user — `load()` gates on this and the trigger effect re-runs on it. */
  user: AuthUser | null;
  /** Active workspace — scopes the list URLs + labels the error messages. */
  activeWorkspace: Workspace | null;
  /** `isLoading` from `useAuth()` — the load-trigger effect no-ops until auth resolves. */
  authLoading: boolean;
  /** App router (`useRouter()` from `@/i18n/navigation`) — drives `openProjectChat`. */
  router: ReturnType<typeof useRouter>;
  /** `showToast` from `useToast()` — star success/failure feedback. */
  showToast: ReturnType<typeof useToast>['showToast'];
  /** The `home.toast` translator. */
  tToast: ReturnType<typeof useTranslations>;
  /** The provider-owned mounted flag — `load()`'s finally guards `setIsInitialLoad` on it. */
  isMountedRef: MutableRefObject<boolean>;
}

interface UseDashboardProjectsResult {
  projects: ProjectSummary[];
  projectFolders: ProjectFolder[];
  isInitialLoad: boolean;
  projectsLoadError: string | null;
  load: () => Promise<void>;
  applyUpdatedProject: (project: ProjectSummary) => void;
  toggleProjectStar: (project: ProjectSummary) => Promise<void>;
  openProjectChat: (project: ProjectSummary) => void;
  /** Raw setter exposed so the provider's folder actions can mutate the folder list. */
  setProjectFolders: React.Dispatch<React.SetStateAction<ProjectFolder[]>>;
  /** Exposed so the provider's `handleMoveProjectToFolder` normalizes with the same fn. */
  normalizeProjectPayload: (project: any) => ProjectSummary;
}

/**
 * `useDashboardProjects` — owns the dashboard's project + folder list state and
 * the loader that fills it.
 *
 * Extracted verbatim from `DashboardContext` (Phase 1.6 (5)). The normalize and
 * sort helpers, `load()` (with its no-user gate, the `Promise.allSettled`
 * projects+folders fan-out, every per-result error branch, and the
 * `isMountedRef`-guarded `setIsInitialLoad` finally), the microtask-deferred
 * load-trigger effect, `applyUpdatedProject`, `toggleProjectStar` (optimistic +
 * server reconcile + revert), and `openProjectChat` (optimistic touch +
 * `router.push`) are byte-for-byte identical to the originals — only the
 * provider-scoped values (`user`, `activeWorkspace`, `authLoading`, `router`,
 * `showToast`, `tToast`, `isMountedRef`) are injected as params. `load` is
 * returned under its internal name so the provider keeps `reload: load` and
 * `handleMoveProjectToFolder` unchanged; `setProjectFolders` /
 * `normalizeProjectPayload` are surfaced for the still-in-provider folder actions.
 */
export function useDashboardProjects({
  user,
  activeWorkspace,
  authLoading,
  router,
  showToast,
  tToast,
  isMountedRef,
}: UseDashboardProjectsParams): UseDashboardProjectsResult {
  // ── Data State ──────────────────────────────────────────────────────────────

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectFolders, setProjectFolders] = useState<ProjectFolder[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [projectsLoadError, setProjectsLoadError] = useState<string | null>(null);

  // Monotonic load-sequence guard. Every `load()` (mount, workspace switch, or an
  // external `reload()`) bumps this and captures its own sequence number; after the
  // awaited fan-out it only commits if it is still the latest invocation. A
  // workspace A→B switch therefore makes A's slow, stale response a no-op instead
  // of letting it overwrite B's fresh projects/folders on a last-response-wins race.
  const loadSeqRef = useRef(0);

  // ── Normalize helpers ───────────────────────────────────────────────────────

  const normalizeProjectPayload = useCallback((project: any): ProjectSummary => {
    return {
      id: project.id,
      userId: project.ownerUserId ?? project.owner_user_id ?? project.userId ?? project.user_id ?? null,
      folderId: project.folderId ?? project.folder_id ?? null,
      orgId: project.orgId ?? project.org_id ?? null,
      name: project.name,
      description: project.description ?? null,
      status: project.status,
      previewUrl: project.previewUrl ?? project.preview_url ?? null,
      createdAt: project.createdAt ?? project.created_at ?? new Date().toISOString(),
      updatedAt: project.updatedAt ?? project.updated_at,
      lastActiveAt: project.lastActiveAt ?? project.last_active_at ?? null,
      lastMessageAt: project.lastMessageAt ?? project.last_message_at ?? null,
      initialPrompt: project.initialPrompt ?? project.initial_prompt ?? null,
      services: project.services,
      aiModel: project.aiModel ?? null,
      thumbnailUrl: project.thumbnailUrl ?? project.thumbnail_url ?? null,
      isStarred: project.isStarred ?? project.is_starred ?? false,
      visibility: project.visibility ?? project.visibility_scope ?? null,
      isPublished: project.isPublished ?? project.is_published ?? false,
    };
  }, []);

  const normalizeProjectsPayload = useCallback(
    (payload: unknown): ProjectSummary[] => {
      const items: unknown[] = Array.isArray((payload as { data?: unknown })?.data)
        ? ((payload as { data: unknown[] }).data ?? [])
        : Array.isArray(payload)
          ? payload
          : [];

      return items
        .filter((p): p is Record<string, unknown> => Boolean(p && typeof p === 'object'))
        .map((p) => normalizeProjectPayload(p));
    },
    [normalizeProjectPayload],
  );

  const normalizeProjectFolderPayload = useCallback((folder: any): ProjectFolder => {
    const rawParentFolderId =
      typeof folder?.parentFolderId === 'string'
        ? folder.parentFolderId
        : typeof folder?.parent_folder_id === 'string'
          ? folder.parent_folder_id
          : null;
    const parentFolderId = rawParentFolderId && rawParentFolderId.trim() ? rawParentFolderId.trim() : null;

    return {
      id: typeof folder?.id === 'string' ? folder.id : '',
      orgId:
        typeof folder?.orgId === 'string' ? folder.orgId : typeof folder?.org_id === 'string' ? folder.org_id : null,
      ownerUserId:
        typeof folder?.ownerUserId === 'string'
          ? folder.ownerUserId
          : typeof folder?.owner_user_id === 'string'
            ? folder.owner_user_id
            : '',
      name: typeof folder?.name === 'string' ? folder.name : '',
      visibility: folder?.visibility === 'workspace' ? 'workspace' : 'personal',
      parentFolderId,
      createdAt:
        typeof folder?.createdAt === 'string'
          ? folder.createdAt
          : typeof folder?.created_at === 'string'
            ? folder.created_at
            : new Date().toISOString(),
      updatedAt:
        typeof folder?.updatedAt === 'string'
          ? folder.updatedAt
          : typeof folder?.updated_at === 'string'
            ? folder.updated_at
            : new Date().toISOString(),
    };
  }, []);

  // ── Sort helpers ────────────────────────────────────────────────────────────

  const getProjectLastUsedTimestamp = useCallback((project: ProjectSummary): number => {
    const lastUsedDate = project.lastActiveAt ?? project.lastMessageAt ?? project.createdAt ?? null;
    if (!lastUsedDate) return 0;
    const hasTimezone =
      lastUsedDate.endsWith('Z') || lastUsedDate.includes('+') || Boolean(lastUsedDate.match(/[-+]\d{2}:\d{2}$/));
    const parsed = new Date(hasTimezone ? lastUsedDate : `${lastUsedDate}Z`).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const sortProjectsByLastUsed = useCallback(
    (items: ProjectSummary[]) =>
      [...items].sort((left, right) => getProjectLastUsedTimestamp(right) - getProjectLastUsedTimestamp(left)),
    [getProjectLastUsedTimestamp],
  );

  // ── Load ────────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    // Claim this invocation's sequence number up front; any in-flight stale load
    // (older seq) is now invalidated and will bail before committing.
    const mySeq = ++loadSeqRef.current;

    // Bug 3 (anonymous home page): gate on the resolved `user` object instead
    // of the legacy `authRequired && !isAuthenticated` pair. The legacy gate
    // could fall through when `authRequired` raced to `false` (e.g. /api/auth/me
    // unreachable), which fired /api/projects + /api/project-folders for
    // anonymous visitors and 401d on the home page.
    if (!user) {
      setProjects([]);
      setProjectFolders([]);
      setProjectsLoadError(null);
      return;
    }

    try {
      const orgId = activeWorkspace?.id;
      const projectsUrl = orgId ? `/api/projects?orgId=${encodeURIComponent(orgId)}` : `/api/projects`;
      const foldersUrl = orgId ? `/api/project-folders?orgId=${encodeURIComponent(orgId)}` : `/api/project-folders`;

      const [projectsResult, foldersResult] = await Promise.allSettled([
        fetchWithAuth(projectsUrl, { cache: 'no-store' }),
        fetchWithAuth(foldersUrl, { cache: 'no-store' }),
      ]);

      let nextProjects: ProjectSummary[] | null = null;
      let nextProjectsError: string | null = null;

      if (projectsResult.status === 'fulfilled') {
        const r = projectsResult.value;
        const payload = await r.json().catch(() => null);
        if (!r.ok) {
          console.warn('Failed to load projects: HTTP', r.status);
          nextProjectsError = extractApiErrorMessage(
            payload,
            `Failed to load projects for ${activeWorkspace?.displayName ?? 'this workspace'}.`,
          );
        } else if (payload?.success === false) {
          console.error('Failed to load projects:', payload?.error || payload?.message);
          nextProjectsError = extractApiErrorMessage(
            payload,
            `Failed to load projects for ${activeWorkspace?.displayName ?? 'this workspace'}.`,
          );
        } else {
          nextProjects = sortProjectsByLastUsed(normalizeProjectsPayload(payload));
        }
      } else {
        console.warn('Failed to load projects:', projectsResult.reason);
        nextProjectsError = `Failed to load projects for ${activeWorkspace?.displayName ?? 'this workspace'}.`;
      }

      // Staleness guard: if a newer load() (e.g. a workspace switch) started
      // while this one's fan-out was in flight, drop this stale invocation's
      // results rather than overwrite the fresher workspace's data.
      if (loadSeqRef.current !== mySeq) return;

      if (nextProjects !== null) {
        setProjects(nextProjects);
        setProjectsLoadError(null);
      } else if (nextProjectsError) {
        setProjectsLoadError(nextProjectsError);
      }

      if (foldersResult.status === 'fulfilled') {
        const r = foldersResult.value;
        if (!r.ok) {
          console.warn('Failed to load folders: HTTP', r.status);
          setProjectFolders([]);
        } else {
          const payload = await r.json().catch(() => null);
          // Re-check after the folders body await: another load() may have
          // superseded this one in the gap.
          if (loadSeqRef.current !== mySeq) return;
          if (payload?.success === false) {
            console.error('Failed to load folders:', payload?.error || payload?.message);
            setProjectFolders([]);
          } else {
            const items: unknown[] = Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload)
                ? payload
                : [];

            const normalized: ProjectFolder[] = items
              .filter((folder): folder is Record<string, unknown> => Boolean(folder && typeof folder === 'object'))
              .map((folder) => normalizeProjectFolderPayload(folder))
              .filter((folder) => folder.id && folder.name);

            setProjectFolders(normalized);
          }
        }
      } else {
        console.warn('Failed to load folders:', foldersResult.reason);
        setProjectFolders([]);
      }
    } catch (error) {
      console.warn('Failed to load library data:', error);
      setProjectsLoadError(`Failed to load projects for ${activeWorkspace?.displayName ?? 'this workspace'}.`);
      setProjectFolders([]);
    } finally {
      // Only the latest load flips the initial-load flag: a superseded stale
      // load must not announce "loaded" while the active load is still pending.
      if (isMountedRef.current && loadSeqRef.current === mySeq) {
        setIsInitialLoad(false);
      }
    }
    // `isMountedRef` is a stable injected ref (provider-owned); listing it is a
    // no-op for re-creation but satisfies exhaustive-deps now that it is a param
    // rather than an in-scope `useRef` the rule recognizes as stable.
  }, [
    activeWorkspace,
    user,
    normalizeProjectFolderPayload,
    normalizeProjectsPayload,
    sortProjectsByLastUsed,
    isMountedRef,
  ]);

  // Trigger load on mount and when workspace / auth state changes.
  // Microtask-defer the call so the synchronous-during-effect setState
  // chain inside load()'s no-user reset branch becomes async-callback
  // setState. External reload() callers run from event handlers and are
  // exempt regardless; this defer only affects mount/workspace-change.
  useEffect(() => {
    if (authLoading) return;
    Promise.resolve().then(() => {
      void load();
    });
  }, [authLoading, activeWorkspace?.id, user, load]);

  // ── Project Actions ──────────────────────────────────────────────────────────

  const applyUpdatedProject = useCallback(
    (updatedProject: ProjectSummary) => {
      setProjects((prev) =>
        sortProjectsByLastUsed(
          prev.map((item) => (item.id === updatedProject.id ? { ...item, ...updatedProject } : item)),
        ),
      );
    },
    [sortProjectsByLastUsed],
  );

  const toggleProjectStar = useCallback(
    async (project: ProjectSummary) => {
      const nextIsStarred = !project.isStarred;
      setProjects((prev) =>
        prev.map((item) => (item.id === project.id ? { ...item, isStarred: nextIsStarred } : item)),
      );

      try {
        const response = await fetchWithAuth(`/api/projects/${project.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isStarred: nextIsStarred }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Failed to update project star' }));
          throw new Error(
            (errorData as { detail?: string; message?: string }).detail ||
              (errorData as { message?: string }).message ||
              'Failed to update project star',
          );
        }

        const payload = await response.json().catch(() => null);
        const updatedProject =
          payload?.data && typeof payload.data === 'object' ? normalizeProjectPayload(payload.data) : null;

        if (updatedProject) {
          setProjects((prev) =>
            prev.map((item) => (item.id === updatedProject.id ? { ...item, ...updatedProject } : item)),
          );
        }

        showToast(nextIsStarred ? tToast('projectAddedToStarred') : tToast('projectRemovedFromStarred'), 'success');
      } catch (error) {
        console.warn('Failed to update project star:', error);
        setProjects((prev) =>
          prev.map((item) => (item.id === project.id ? { ...item, isStarred: project.isStarred ?? false } : item)),
        );
        showToast(tToast('failedUpdateStar'), 'error');
      }
    },
    [normalizeProjectPayload, showToast, tToast],
  );

  const openProjectChat = useCallback(
    (project: ProjectSummary) => {
      // Optimistically mark the project as recently touched
      const touchedAt = new Date().toISOString();
      setProjects((prev) =>
        sortProjectsByLastUsed(
          prev.map((item) => (item.id === project.id ? { ...item, lastActiveAt: touchedAt } : item)),
        ),
      );
      void fetchWithAuth(`/api/projects/${project.id}/touch`, { method: 'POST' }).catch(() => {
        // best-effort
      });
      router.push(`/${project.id}/chat`);
    },
    [router, sortProjectsByLastUsed],
  );

  return {
    projects,
    projectFolders,
    isInitialLoad,
    projectsLoadError,
    load,
    applyUpdatedProject,
    toggleProjectStar,
    openProjectChat,
    setProjectFolders,
    normalizeProjectPayload,
  };
}
